import type { ClipboardEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { useAuthPageTheme } from '@/hooks/useAuthPageTheme';
import { authService } from '@/services/auth.service';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { AuthError } from '@/types/auth';
import { Trans, useTranslation } from 'react-i18next';
import { resolveSigninRedirectUrl } from '@/lib/auth/signin-redirect';
import { normalizeEmail } from '@/lib/email';

const CODE_LENGTH = 6;

export default function CustomerVerify() {
  useAuthPageTheme();
  const { hydrateFromResponse } = useCustomerAuth();
  const provider = useProvider();
  const brandLogo = useBrandLogo();
  const navigate = useNavigate();
  const location = useLocation();
  const email = normalizeEmail((location.state as { email?: string } | undefined)?.email ?? '');
  const { t } = useTranslation();
  const brandName = provider?.name ?? provider?.slug ?? t('customer.dashboard.providerFallback');

  const [code, setCode] = useState(Array<string>(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [countdown, setCountdown] = useState(60);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (!email) {
      navigate('/customer/login', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendDisabled && countdown > 0) {
      const timer = setTimeout(() => setCountdown((value) => value - 1), 1000);
      return () => clearTimeout(timer);
    }

    if (countdown === 0) {
      setResendDisabled(false);
    }
  }, [countdown, resendDisabled]);

  const handleChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const nextCode = [...code];
    nextCode[index] = value;
    setCode(nextCode);
    setError('');

    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (nextCode.every((digit) => digit !== '')) {
      void handleVerify(nextCode.join(''));
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').slice(0, CODE_LENGTH);
    if (!/^\d+$/.test(pasted)) {
      return;
    }

    const nextCode = pasted.split('');
    while (nextCode.length < CODE_LENGTH) {
      nextCode.push('');
    }

    setCode(nextCode);
    inputRefs.current[Math.min(CODE_LENGTH - 1, pasted.length - 1)]?.focus();

    if (pasted.length === CODE_LENGTH) {
      void handleVerify(pasted);
    }
  };

  const handleVerify = async (verificationCode: string) => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await authService.verifyCustomerOTP({ email, code: verificationCode });
      await hydrateFromResponse(response);
      const redirectUrl = resolveSigninRedirectUrl(response);
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }
      navigate('/customer', { replace: true });
    } catch (err) {
      const axiosError = err as AxiosError<AuthError>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        t('customer.portal.verify.error');
      setError(message);
      setCode(Array<string>(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResendDisabled(true);
    setCountdown(60);
    setError('');
    setCode(Array<string>(CODE_LENGTH).fill(''));
    inputRefs.current[0]?.focus();

    try {
      await authService.sendCustomerOTP({ email });
    } catch (err) {
      const axiosError = err as AxiosError<AuthError>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        t('customer.portal.verify.resendError');
      setError(message);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              className="mx-auto h-10 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <span className="mx-auto text-2xl font-semibold tracking-tight text-foreground">
              {brandName}
            </span>
          )}
          <CardTitle>{t('customer.portal.verify.title')}</CardTitle>
          <CardDescription>
            <Trans
              i18nKey="customer.portal.verify.description"
              values={{ email }}
              components={{ strong: <span className="font-medium text-foreground" /> }}
            />
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex justify-center gap-2">
            {code.map((digit, index) => (
              <Input
                key={`otp-${index}`}
                ref={(element) => {
                  inputRefs.current[index] = element;
                }}
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(event) => handleChange(index, event.target.value)}
                onKeyDown={(event) => handleKeyDown(index, event)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="h-12 w-12 text-center text-lg font-semibold"
                disabled={isSubmitting}
              />
            ))}
          </div>

          {error ? <p className="text-center text-sm text-destructive">{error}</p> : null}

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <span>{t('customer.portal.verify.resendPrompt')}</span>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={handleResend}
              disabled={resendDisabled}
            >
              {resendDisabled
                ? t('customer.portal.verify.resendTimer', { seconds: countdown })
                : t('customer.portal.verify.resendCta')}
            </Button>
          </div>

          <Button
            className="w-full"
            onClick={() => handleVerify(code.join(''))}
            disabled={isSubmitting || code.some((digit) => !digit)}
          >
            {isSubmitting
              ? t('customer.portal.verify.verifying')
              : t('customer.portal.verify.verifyCta')}
          </Button>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate('/customer/login')}
            disabled={isSubmitting}
          >
            {t('customer.portal.verify.useDifferentEmail')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
